// useListNodelistHydration.js

import { useCallback, useMemo } from 'react';

import useNodesByKeysQuery from 'hooks/useNodesByKeysQuery';
import { isRelationFieldType } from 'utilities/FieldTypes';
import {
  getNodelistNodeTitle,
  normalizeNodelistKeys,
} from 'utilities/NodelistField';
import { resolveSchemaFieldValue } from 'views/utils/fieldValueResolver';

/**
 * useListNodelistHydration
 *
 * Batches related-node titles for Relation fields shown on list items.
 *
 * @param {object} params
 * @param {object[]|null|undefined} params.nodes - Visible list nodes.
 * @param {function} params.getItemConfig - Returns list item config for a node.
 *
 * @returns {{ resolveNodelistTitle: (nodeKey: string) => string }}
 */
const useListNodelistHydration = ({
  nodes,
  getItemConfig,
} = {}) => {
  const relatedKeys = useMemo(() => {
    if (typeof getItemConfig !== 'function') return [];

    const keys = new Set();
    (Array.isArray(nodes) ? nodes : []).forEach((node) => {
      const config = getItemConfig(node);
      const schemaFieldMap = config?.schemaFieldMap;
      const listFields = Array.isArray(config?.listFields) ? config.listFields : [];
      if (!(schemaFieldMap instanceof Map) || !listFields.length) return;

      listFields.forEach((fieldKey) => {
        const fieldDef = schemaFieldMap.get(fieldKey);
        if (!isRelationFieldType(fieldDef)) return;

        normalizeNodelistKeys(resolveSchemaFieldValue({
          item: node,
          fieldDef,
          typeKey: config?.typeDef?.key,
          dataSection: config?.typeDef?.dataSection,
        })).forEach((key) => keys.add(key));
      });
    });

    return [...keys];
  }, [getItemConfig, nodes]);

  const { nodesByKey, isLoading } = useNodesByKeysQuery(relatedKeys, {
    enabled: relatedKeys.length > 0,
  });

  const resolveNodelistTitle = useCallback((nodeKey) => {
    const key = String(nodeKey || '').trim();
    if (!key) return '';

    const related = nodesByKey?.get?.(key);
    if (related) return getNodelistNodeTitle(related);

    if (isLoading) return '';

    return 'Unavailable';
  }, [isLoading, nodesByKey]);

  return { resolveNodelistTitle };
};

export default useListNodelistHydration;
